import { useContext, useEffect, useState } from "react";
import "../Announcement/Announcement.scss";
import { toast } from "react-toastify";
import { confirmDialog } from "primereact/confirmdialog";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import {
  inputStyles,
  labelStyles,
  styles,
} from "../Announcement/AnnouncementStyles";
import InputBox from "../../../components/Fields/Input Box/InputBox";
import { achieverDetailsFields } from "./AchieversGalleryFields";
import { AppContext } from "../../../context/AppContext";
import api from "../../../api.js";
import Loader from "../../../components/Loader/Loader.jsx";

const Achievers_Gallrey = () => {
  const { backendUrl } = useContext(AppContext);
  const [allAchieversDetails, setAllAchieversDetails] = useState([]);
  const [achieverDetails, setAchieverDetails] = useState({
    name: "",
    education: "",
    batch: "",
    currentDesignation: "",
    companyName: "",
    additionalQualification: "",
    location: "",
    department: "",
    achievement: "",
    email: "",
    contact: "",
    imageFile: null,
  });
  const [selectedAchieverDetails, setSelectedAchieverDetails] = useState(null);
  const [updatedAchieverDetails, setUpdatedAchieverDetails] = useState({});
  const [visible, setVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [scrollHeight, setScrollHeight] = useState("425px");
  let errorShown = false;

  useEffect(() => {
    const handleResize = () => {
      const screenWidth = window.innerHeight;
      if (screenWidth >= 900) {
        setScrollHeight("650px");
      } else {
        setScrollHeight("440px");
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fieldLabels = {
    name: "Name",
    education: "Education",
    batch: "Batch",
    currentDesignation: "Current Designation",
    companyName: "Company Name",
    imageFile: "Image File",
  };

  const validateField = (name, value, isEditMode = false) => {
    if (name === "imageFile" && isEditMode && !value) {
      return "";
    }

    const mandatoryFields = [
      "name",
      "education",
      "batch",
      "currentDesignation",
      "companyName",
    ];
    if (mandatoryFields.includes(name) && !value?.trim()) {
      return `${fieldLabels[name]} is required`;
    }

    switch (name) {
      case "email":
        if (
          value &&
          value.trim() &&
          !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
        ) {
          return "Please enter a valid email address";
        }
        break;
      case "contact":
        if (value && value.trim() && !/^\+\d{1,3}\d{10}$/.test(value)) {
          return "Contact must be a 10-digit number";
        }
        break;
      case "imageFile":
        if (!isEditMode && !value) {
          return "Image is required";
        }
        if (value) {
          const allowedTypes = [
            "image/jpeg",
            "image/png",
            "image/gif",
            "image/webp",
            "image/svg+xml",
          ];
          if (!allowedTypes.includes(value.type)) {
            return "Only JPG, PNG, GIF, WEBP, and SVG images are allowed";
          }
          const maxSize = 25 * 1024 * 1024;
          if (value.size > maxSize) {
            return "Image size must be less than 25MB";
          }
        }
        break;
    }
    return "";
  };

  const validateForm = (formData, isEditMode = false) => {
    const errors = {};
    let isValid = true;
    const mandatoryFields = [
      "name",
      "education",
      "batch",
      "currentDesignation",
      "companyName",
    ];
    if (!isEditMode) {
      mandatoryFields.push("imageFile");
    }
    mandatoryFields.forEach((field) => {
      const error = validateField(field, formData[field], isEditMode);
      if (error) {
        errors[field] = error;
        isValid = false;
      }
    });
    [
      "email",
      "contact",
      "additionalQualification",
      "location",
      "department",
      "achievement",
    ].forEach((field) => {
      const error = validateField(field, formData[field], isEditMode);
      if (error) {
        errors[field] = error;
      }
    });
    setValidationErrors(errors);
    return isValid;
  };

  async function fetchAllAchieversDetails() {
    try {
      const { data } = await api.get(
        `${backendUrl}/api/auth/getAllAchieversImages`
      );
      if (data.success) {
        setAllAchieversDetails(data || []);
      }
      errorShown = false;
    } catch (error) {
      if (errorShown) return;
      errorShown = true;
      if (error.response && error.response.status === 503) {
        toast.error(
          "⚠️ The service is currently under maintenance. Please try again later."
        );
      } else if (!navigator.onLine) {
        toast.error("🚫 No internet connection. Please check your network.");
      } else if (error.request && !error.response) {
        toast.error(
          "⚠️ Network issue or server is unreachable. Try again later."
        );
      } else if (error.response && error.response.status === 400) {
        toast.error(
          error.response.data.message ||
            "⚠️ Bad request. Please check the request format."
        );
      } else {
        console.error("Unexpected error:", error);
        toast.error("Something went wrong.");
      }
    }
  }

  useEffect(() => {
    fetchAllAchieversDetails();
  }, []);

  async function uploadAnAchieverImage(e) {
    e.preventDefault();
    setIsSubmitting(true);

    if (!validateForm(achieverDetails)) {
      setIsSubmitting(false);
      return;
    }

    const formData = new FormData();

    formData.append("imageFile", achieverDetails.imageFile);
    formData.append("name", achieverDetails.name || "-");
    formData.append("education", achieverDetails.education || "-");
    formData.append("batch", achieverDetails.batch || "-");
    formData.append("companyName", achieverDetails.companyName || "-");
    formData.append(
      "currentDesignation",
      achieverDetails.currentDesignation || "-"
    );

    formData.append(
      "additionalQualification",
      achieverDetails.additionalQualification || "-"
    );
    formData.append("location", achieverDetails.location || "-");
    formData.append("department", achieverDetails.department || "-");
    formData.append("achievement", achieverDetails.achievement || "-");

    formData.append("email", achieverDetails.email || "");
    formData.append("contact", achieverDetails.contact || "");

    try {
      const { data } = await api.post(
        `${backendUrl}/api/auth/uploadAnAchieverImage`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (data.success) {
        toast.success("Achiever details uploaded successfully");
        fetchAllAchieversDetails();
        resetForm();
        setVisible(false);
      }
    } catch (error) {
      if (error.response && error.response.status === 503) {
        toast.error(
          "⚠️ The service is currently under maintenance. Please try again later."
        );
      } else if (error.response && error.response.status === 400) {
        toast.error(error.response.data.message);
      } else if (error.response && error.response.status === 404) {
        toast.error(error.response.data.message);
      } else if (error.response && error.response.status === 500) {
        toast.error(error.response.data.message);
      } else if (!navigator.onLine) {
        toast.error("🚫 No internet connection. Please check your network.");
      } else if (error.request && !error.response) {
        toast.error(
          "⚠️ Network issue or server is unreachable. Try again later."
        );
      } else {
        console.error("Unexpected error:", error);
        toast.error("Something went wrong.");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  const resetForm = () => {
    setAchieverDetails({
      name: "",
      education: "",
      batch: "",
      currentDesignation: "",
      companyName: "",
      additionalQualification: "",
      location: "",
      department: "",
      achievement: "",
      email: "",
      contact: "",
      imageFile: null,
    });
    setValidationErrors({});
  };

  const showConfirmDialog = (rowData) => {
    confirmDialog({
      message: (
        <div className="custom-dialog-content">
          <div className="iconParent">
            <i className="pi pi-times"></i>
          </div>
          <div className="contentParent">
            <h2>Are you sure?</h2>
            <div>
              <p>
                Are you sure you want to delete the record of{" "}
                <strong>{rowData.name}</strong>?
              </p>
              <p>This action cannot be undone.</p>
            </div>
          </div>
        </div>
      ),
      header: "",
      icon: null,
      acceptLabel: "Delete",
      rejectLabel: "Cancel",
      acceptClassName: "p-button-danger custom-delete-btn",
      rejectClassName: "p-button-secondary custom-cancel-btn",
      accept: () => {
        handleDeleteClick(rowData);
      },
    });
  };

  async function handleDeleteClick(rowData) {
    try {
      const { data } = await api.delete(
        `${backendUrl}/api/auth/deleteAnAchieverImage/${rowData.id}`
      );
      if (data.success) {
        toast.success(`${rowData.name}'s record deleted successfully`);
        fetchAllAchieversDetails();
      }
    } catch (error) {
      if (error.response && error.response.status === 503) {
        toast.error(
          "⚠️ The service is currently under maintenance. Please try again later."
        );
      } else if (!navigator.onLine) {
        toast.error("🚫 No internet connection. Please check your network.");
      } else if (error.request && !error.response) {
        toast.error(
          "⚠️ Network issue or server is unreachable. Try again later."
        );
      } else {
        console.error("Unexpected error:", error);
        toast.error("Something went wrong.");
      }
    }
  }

  const handleEditClick = (rowData) => {
    setSelectedAchieverDetails(rowData);
    setUpdatedAchieverDetails({
      name: rowData.name,
      education: rowData.education,
      batch: rowData.batch,
      currentDesignation: rowData.currentDesignation,
      companyName: rowData.companyName,
      additionalQualification: rowData.additionalQualification,
      location: rowData.location,
      department: rowData.department,
      achievement: rowData.achievement,
      email: rowData.email,
      contact: rowData.contact,
      file: null,
    });
    setVisible(true);
    setValidationErrors({});
  };

  const updateAchieverDetails = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!validateForm(updatedAchieverDetails, true)) {
      setIsSubmitting(false);
      return;
    }

    try {
      const formData = new FormData();
      if (updatedAchieverDetails.file) {
        formData.append("imageFile", updatedAchieverDetails.file);
      }
      formData.append("name", updatedAchieverDetails.name || "-");
      formData.append("education", updatedAchieverDetails.education || "-");
      formData.append("batch", updatedAchieverDetails.batch || "-");
      formData.append("companyName", updatedAchieverDetails.companyName || "-");
      formData.append(
        "currentDesignation",
        updatedAchieverDetails.currentDesignation || "-"
      );
      formData.append(
        "additionalQualification",
        updatedAchieverDetails.additionalQualification || "-"
      );
      formData.append("location", updatedAchieverDetails.location || "-");
      formData.append("department", updatedAchieverDetails.department || "-");
      formData.append("achievement", updatedAchieverDetails.achievement || "-");
      formData.append("email", updatedAchieverDetails.email || "");
      formData.append("contact", updatedAchieverDetails.contact || "");
      const { data } = await api.put(
        `${backendUrl}/api/auth/updateAnAchieverImage/${selectedAchieverDetails.id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (data.success) {
        toast.success("Achiever details updated successfully");
        fetchAllAchieversDetails();
        setVisible(false);
        setSelectedAchieverDetails(null);
        setUpdatedAchieverDetails({});
      }
    } catch (error) {
      if (errorShown) return;
      errorShown = true;
      if (!navigator.onLine) {
        toast.error("🚫 No internet connection. Please check your network.");
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.request && !error.response) {
        toast.error(
          "⚠️ Network issue or server is unreachable. Try again later."
        );
      } else if (error.response && error.response.status === 400) {
        toast.error(error.response.data.message);
      } else if (error.response && error.response.status === 404) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Something went wrong.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleStatus = async (rowData) => {
    setIsSubmitting(true);
    try {
      await api.patch(
        `${backendUrl}/api/auth/toggleAchieverDetailsStatus/${rowData.id}`,
        { isActive: !rowData.isActive }
      );
      fetchAllAchieversDetails();
    } catch (error) {
      if (error.response && error.response.status === 503) {
        toast.error(
          "⚠️ The service is currently under maintenance. Please try again later."
        );
      } else if (!navigator.onLine) {
        toast.error("🚫 No internet connection. Please check your network.");
      }
      if (error.response && error.response.status === 404) {
        toast.error(
          "⚠️ The service is currently under maintenance. Please try again later."
        );
      } else if (error.request && !error.response) {
        toast.error(
          "⚠️ Network issue or server is unreachable. Try again later."
        );
      } else {
        console.error("Unexpected error:", error);
        toast.error("Something went wrong.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    if (selectedAchieverDetails) {
      setUpdatedAchieverDetails((prev) => ({ ...prev, [fieldName]: value }));
    } else {
      setAchieverDetails((prev) => ({ ...prev, [fieldName]: value }));
    }
    if (validationErrors[fieldName]) {
      setValidationErrors((prev) => ({ ...prev, [fieldName]: "" }));
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    const isEditMode = !!selectedAchieverDetails;
    const error = validateField("imageFile", file, isEditMode);

    if (error) {
      setValidationErrors((prev) => ({ ...prev, imageFile: error }));
      e.target.value = "";
      return;
    }

    if (selectedAchieverDetails) {
      setUpdatedAchieverDetails((prev) => ({ ...prev, file: file }));
    } else {
      setAchieverDetails((prev) => ({ ...prev, imageFile: file }));
    }

    setValidationErrors((prev) => ({ ...prev, imageFile: "" }));
  };

  return (
    <div className="adminAchieversGallreyMainContainer">
      <Loader visible={isSubmitting} />

      <div className="headerParent">
        <h2>Welcome to Admin Achievers Gallery Management</h2>
      </div>

      <Dialog
        header={
          selectedAchieverDetails ? "Edit Achiever Details" : "Add New Achiever"
        }
        visible={visible}
        style={{ width: "40vw" }}
        onHide={() => {
          setVisible(false);
          setSelectedAchieverDetails(null);
          setUpdatedAchieverDetails({});
          resetForm();
        }}
        draggable={false}
        resizable={false}
        modal
        className="customAnnouncementDialog"
      >
        <form
          onSubmit={
            selectedAchieverDetails
              ? updateAchieverDetails
              : uploadAnAchieverImage
          }
        >
          {achieverDetailsFields.map((field) => (
            <div key={field.fieldName} className="field-container">
              <InputBox
                styles={styles}
                inputStyles={inputStyles}
                labelStyles={labelStyles}
                fieldName={field.fieldName}
                customLabelName={field.customLabelName}
                placeholder={field.placeholder}
                type={field.type}
                required={[
                  "name",
                  "education",
                  "batch",
                  "currentDesignation",
                  "companyName",
                ].includes(field.fieldName)}
                value={
                  selectedAchieverDetails
                    ? updatedAchieverDetails[field.fieldName] || ""
                    : achieverDetails[field.fieldName] || ""
                }
                handleChange={(e) =>
                  handleFieldChange(field.fieldName, e.target.value)
                }
                onBlur={(e) => {
                  const error = validateField(
                    field.fieldName,
                    e.target.value,
                    !!selectedAchieverDetails
                  );
                  setValidationErrors((prev) => ({
                    ...prev,
                    [field.fieldName]: error,
                  }));
                }}
              />
              {validationErrors[field.fieldName] && (
                <div className="validation-error">
                  <i className="pi pi-exclamation-circle"></i>
                  {validationErrors[field.fieldName]}
                </div>
              )}
            </div>
          ))}

          <div className="inputFieldWrapper">
            <label style={labelStyles}>
              {selectedAchieverDetails
                ? "Update Image (Optional)"
                : "Upload Image"}
              {!selectedAchieverDetails && (
                <span className="required-asterisk" style={{ color: "red" }}>
                  *
                </span>
              )}
            </label>
            <input
              type="file"
              accept=".jpg,.jpeg,.png,.gif,.webp,.svg"
              style={inputStyles}
              onChange={handleFileUpload}
            />
            {validationErrors.imageFile && (
              <div className="validation-error">
                <i className="pi pi-exclamation-circle"></i>
                {validationErrors.imageFile}
              </div>
            )}
          </div>

          <div className="submitBtnParent">
            <button
              type="submit"
              className="submitButton"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <i className="pi pi-spinner pi-spin"></i> Processing...
                </>
              ) : selectedAchieverDetails ? (
                "Update Details"
              ) : (
                "Add Achiever"
              )}
            </button>
          </div>
        </form>
      </Dialog>

      <div className="uploadBtnsParent">
        <Button
          label="Add New Achiever"
          onClick={() => setVisible(true)}
          className="uploadBtn"
        />
      </div>

      <article className="tableWrapper">
        <div className="scrollableTableContainer achieversDetailsTable">
          <DataTable
            value={allAchieversDetails.achieversDetails || []}
            showGridlines
            stripedRows
            className="table"
            emptyMessage="No achievers to display at the moment."
            paginator
            rows={5}
            rowsPerPageOptions={[5, 10, 20]}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} achievers"
            loading={!allAchieversDetails.achieversDetails}
            scrollable
            scrollHeight={scrollHeight}
            scrollDirection="horizontal"
            columnResizeMode="expand"
          >
            <Column
              header="S.No"
              body={(_, options) => options.rowIndex + 1}
              className="tableData"
              headerStyle={{ whiteSpace: "nowrap" }}
              bodyStyle={{ whiteSpace: "nowrap" }}
            />
            <Column
              field="name"
              header="Name"
              className="tableData"
              style={{ minWidth: "250px", maxWidth: "350px" }}
            />
            <Column
              header="Image"
              className="tableData"
              style={{ minWidth: "275px", maxWidth: "350px" }}
              body={(rowData) => {
                const detail = rowData;
                if (!detail.data) {
                  return <span>No Data</span>;
                }
                const byteCharacters = atob(detail.data);
                const byteNumbers = Array.from(byteCharacters).map((char) =>
                  char.charCodeAt(0)
                );
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], {
                  type: detail.contentType,
                });
                const downloadUrl = URL.createObjectURL(blob);
                return (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <span>{detail.name}</span>
                    <a
                      href={downloadUrl}
                      download={detail.filename}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-pr-tooltip="Download file"
                      data-pr-position="top"
                    >
                      <Button
                        icon="pi pi-download"
                        rounded
                        text
                        severity="info"
                        aria-label="Download"
                        className="download-button"
                      />
                    </a>
                  </div>
                );
              }}
            />
            <Column
              field="education"
              header="Education"
              className="tableData"
              style={{ minWidth: "250px", maxWidth: "350px" }}
            />
            <Column
              field="batch"
              header="Batch"
              className="tableData"
              headerStyle={{ whiteSpace: "nowrap" }}
              bodyStyle={{ whiteSpace: "nowrap" }}
            />
            <Column
              field="companyName"
              header="Organisation Name"
              className="tableData"
              headerStyle={{ whiteSpace: "nowrap" }}
              bodyStyle={{ whiteSpace: "wrap" }}
              style={{ minWidth: "225px", maxWidth: "325px" }}
            />
            <Column
              field="currentDesignation"
              header="Current Designation"
              className="tableData"
              headerStyle={{ whiteSpace: "nowrap" }}
              bodyStyle={{ textWrap: "wrap" }}
              style={{ minWidth: "200px", maxWidth: "325px" }}
            />
            <Column
              field="additionalQualification"
              header="Additional Qualification"
              className="tableData"
              style={{ minWidth: "200px", maxWidth: "325px" }}
            />
            <Column
              field="location"
              header="Location"
              className="tableData"
              style={{ minWidth: "200px", maxWidth: "325px" }}
            />
            <Column
              field="department"
              header="Department"
              className="tableData"
              style={{ minWidth: "200px", maxWidth: "325px" }}
            />
            <Column
              field="achievement"
              header="Achievement"
              className="tableData"
              style={{ minWidth: "200px", maxWidth: "325px" }}
            />
            <Column
              field="email"
              header="Email"
              className="tableData"
              style={{ minWidth: "200px", maxWidth: "325px" }}
            />
            <Column
              field="contact"
              header="Contact Number"
              className="tableData"
              style={{ minWidth: "200px", maxWidth: "325px" }}
            />
            <Column
              field="createdAt"
              header="Created At"
              body={(rowData) =>
                new Date(rowData.createdAt).toLocaleDateString()
              }
              className="tableData"
              headerStyle={{ whiteSpace: "nowrap" }}
              bodyStyle={{ whiteSpace: "nowrap" }}
            />
            <Column
              field="updatedAt"
              header="Updated At"
              body={(rowData) =>
                new Date(rowData.updatedAt).toLocaleDateString()
              }
              className="tableData"
              headerStyle={{ whiteSpace: "nowrap" }}
              bodyStyle={{ whiteSpace: "nowrap" }}
            />
            <Column
              field="status"
              header="Status"
              headerStyle={{ whiteSpace: "nowrap" }}
              bodyStyle={{ whiteSpace: "nowrap" }}
              className="tableData"
              body={(rowData) => (
                <div className="statusBtnParent">
                  <Button
                    label={rowData.isActive ? "Active" : "Deactive"}
                    severity={rowData.isActive ? "success" : "danger"}
                    className="statusBtn"
                    onClick={() => toggleStatus(rowData)}
                  />
                </div>
              )}
              frozen
              alignFrozen="right"
            />
            <Column
              header="Actions"
              headerStyle={{ whiteSpace: "nowrap" }}
              bodyStyle={{ whiteSpace: "nowrap" }}
              body={(rowData) => (
                <div className="action-buttons">
                  <Button
                    label="Edit"
                    severity="success"
                    className="actionBtns"
                    onClick={() => handleEditClick(rowData)}
                  />
                  <Button
                    label="Delete"
                    severity="danger"
                    className="actionBtns"
                    onClick={() => showConfirmDialog(rowData)}
                  />
                </div>
              )}
              className="tableData"
              frozen
              alignFrozen="right"
            />
          </DataTable>
        </div>
      </article>
    </div>
  );
};

export default Achievers_Gallrey;
